import axios from 'axios';
import Cookies from 'universal-cookie';

// ── Estado inicial ─────────────────────────────────────────────────────────────
const initialState = {
    notifications: [],
    totalCount: 0,
    unreadCount: 0,
    loading: false,
    error: null,
};

// ── Action Types ───────────────────────────────────────────────────────────────
const FETCH_NOTIFICATIONS_START   = 'FETCH_NOTIFICATIONS_START';
const FETCH_NOTIFICATIONS_SUCCESS = 'FETCH_NOTIFICATIONS_SUCCESS';
const FETCH_NOTIFICATIONS_ERROR   = 'FETCH_NOTIFICATIONS_ERROR';
const SET_UNREAD_COUNT            = 'SET_UNREAD_COUNT';
const MARK_NOTIFICATION_READ      = 'MARK_NOTIFICATION_READ';
const MARK_ALL_READ               = 'MARK_ALL_READ';

// ── Reducer ────────────────────────────────────────────────────────────────────
export default function notificacionesReducer(state = initialState, action) {
    switch (action.type) {
        case FETCH_NOTIFICATIONS_START:
            return { ...state, loading: true, error: null };

        case FETCH_NOTIFICATIONS_SUCCESS:
            return {
                ...state,
                loading: false,
                notifications: action.payload.items,
                totalCount: action.payload.totalCount,
                unreadCount: action.payload.unreadCount,
            };

        case FETCH_NOTIFICATIONS_ERROR:
            return { ...state, loading: false, error: action.payload };

        case SET_UNREAD_COUNT:
            return { ...state, unreadCount: action.payload };

        case MARK_NOTIFICATION_READ:
            return {
                ...state,
                notifications: state.notifications.map(n =>
                    n.id === action.payload ? { ...n, isRead: true } : n
                ),
                unreadCount: Math.max(0, state.unreadCount - 1),
            };

        case MARK_ALL_READ:
            return {
                ...state,
                notifications: state.notifications.map(n => ({ ...n, isRead: true })),
                unreadCount: 0,
            };

        default:
            return state;
    }
}

// ── Action Creators ────────────────────────────────────────────────────────────
const fetchNotificationsStart = () => ({ type: FETCH_NOTIFICATIONS_START });
const fetchNotificationsSuccess = (data) => ({ type: FETCH_NOTIFICATIONS_SUCCESS, payload: data });
const fetchNotificationsError = (err) => ({ type: FETCH_NOTIFICATIONS_ERROR, payload: err });

// ── Helpers ────────────────────────────────────────────────────────────────────
const getHeaders = () => {
    const cookies = new Cookies();
    return { Authorization: `Bearer ${cookies.get('token')}` };
};

// ── Thunks ─────────────────────────────────────────────────────────────────────

/**
 * Obtiene notificaciones paginadas del usuario autenticado.
 * @param {number} page - Página (default 1)
 * @param {boolean|null} onlyUnread - null = todas, true = solo no leídas
 */
export const fetchNotificationsFromAPI = (page = 1, onlyUnread = null) => async (dispatch) => {
    dispatch(fetchNotificationsStart());
    try {
        const params = { page, pageSize: 20 };
        if (onlyUnread !== null) params.onlyUnread = onlyUnread;

        const response = await axios.get(`${window.API_BASE_URL}/notifications`, {
            params,
            headers: getHeaders(),
        });

        dispatch(fetchNotificationsSuccess(response.data));
    } catch (error) {
        dispatch(fetchNotificationsError(error?.message ?? 'Error al cargar notificaciones'));
    }
};

/**
 * Obtiene solo el conteo de notificaciones no leídas.
 */
export const fetchUnreadCountFromAPI = () => async (dispatch) => {
    try {
        const response = await axios.get(`${window.API_BASE_URL}/notifications/unread-count`, {
            headers: getHeaders(),
        });
        dispatch({ type: SET_UNREAD_COUNT, payload: response.data.count ?? 0 });
    } catch {
        // Silencioso — no interrumpir la UI por un conteo fallido
    }
};

/**
 * Marca una notificación como leída.
 * @param {number} id
 */
export const markNotificationReadInAPI = (id) => async (dispatch) => {
    try {
        await axios.patch(`${window.API_BASE_URL}/notifications/${id}/read`, {}, {
            headers: getHeaders(),
        });
        dispatch({ type: MARK_NOTIFICATION_READ, payload: id });
    } catch {
        // Silencioso
    }
};

/**
 * Marca todas las notificaciones como leídas.
 */
export const markAllNotificationsReadInAPI = () => async (dispatch) => {
    try {
        await axios.patch(`${window.API_BASE_URL}/notifications/read-all`, {}, {
            headers: getHeaders(),
        });
        dispatch({ type: MARK_ALL_READ });
    } catch {
        // Silencioso
    }
};
